import InfoModal from "@/components/core/modals/InfoModal";
import FacturationProgress from "@/components/core/progress/FacturationProgress";
import {
  Bill,
  Facturation,
  Office,
  Stat,
} from "@/type/core/offices/offices.types";
import { Col, Dropdown, Row } from "antd";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { columns as billColumns } from "@views/core/extranet/menu/offices/balanceAndStatistics/columns/BillsColumns";
import { columns as statColumns } from "@views/core/extranet/menu/offices/balanceAndStatistics/columns/StatisticsColumns";
import {
  DISPLAY_PROMO_CODE_BUTTON,
  FEATURE_HAS_BEEN_TESTED_AND_SHOULD_APPEAR,
} from "@utils/Constant";
import BasicButton from "@components/core/buttons/BasicButton";
import ChevronDownIcon from "@static/core/icons/icon-chevron-down.svg?react";

import BasicTable from "@components/core/tables/BasicTable";
import SeeMoreTableWrapper from "@components/core/tables/SeeMoreTableWrapper";
import PromotionCodeModal from "@/components/core/modals/PromotionCodeModal";
import { ROUTES } from "@routes/Routes";
import { useNavigate } from "react-router-dom";

type StatsType = "usage" | "cost";

interface BalanceAndStatisticsTabContentProps {
  office: Office;
  facturation?: Facturation;
  bills: Bill[];
  stats: Stat[];
  setIsOrderModalVisible: (value: boolean) => void;
  selectedYear: number;
  setSelectedYear: (value: number) => void;
}

const BalanceAndStatisticsTabContent: FunctionComponent<
  BalanceAndStatisticsTabContentProps
> = ({
  office,
  facturation,
  bills,
  stats,
  setIsOrderModalVisible,
  selectedYear,
  setSelectedYear,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getEveryYear = (start: number): number[] => {
    const now = new Date().getFullYear();
    const result = [];
    for (let i = start; i <= now; i++) {
      result.push(i);
    }
    return result;
  };

  const checkEmptyString = (value: string | undefined): boolean => {
    return !value || value.trim() === "";
  };

  const checkZipCode = (zipCode: string | undefined) => {
    const regex =
      /^((([1-8][0-9])|(0[1-9])|(9[0-5]))([0-9]{3})|((97[1-6])|(98([6-8]|4)))([0-9]{2}))$/;
    return zipCode && regex.test(zipCode);
  };

  const YEARS = getEveryYear(2018);
  const shouldDisplayMigrationLink = false;
  const fullActiveOffice = office;
  const filledAddress =
    !checkEmptyString(fullActiveOffice.billing?.address1) &&
    !checkEmptyString(fullActiveOffice.billing?.city) &&
    checkZipCode(fullActiveOffice.billing?.zipCode);
  const cguAccepted = fullActiveOffice && fullActiveOffice.cgu;
  const isExpertComptable =
    fullActiveOffice && fullActiveOffice.expertComptable;
  const filteredBills = bills.filter(
    (bill) => bill.officeId === fullActiveOffice.id,
  );
  const filteredStats = stats.filter(
    (stat) => stat.officeId === fullActiveOffice.id,
  );

  const [statsType, setStatsType] = useState<StatsType>("usage");
  const [isPromotionCodeModalVisible, setIsPromotionCodeModalVisible] =
    useState<boolean>(false);

  return (
    <>
      <div className="pt-30 pb-15">
        <div className="p-sm-24">
          <h2>
            {t("offices.balanceAndStatistics.bill")}
            <InfoModal value="solde" info />
          </h2>
          <div className="pt-24">
            {office.belongsToGroup ? (
              t("offices.balanceAndStatistics.belongsToGroup", {
                groupName: office.groupName,
              })
            ) : (
              <>
                <Row gutter={32} className="mt-16 mb-48">
                  <Col span={24} lg={2} xl={4} />
                  <Col span={24} lg={20} xl={16}>
                    <FacturationProgress
                      facturation={facturation?.officesBalance.find(
                        (thisOffice) => thisOffice.officeId === office.id,
                      )}
                      threshold={facturation?.alertThreshold}
                    />
                  </Col>
                  <Col span={24} lg={2} xl={4} />
                </Row>
                <Row gutter={32} className="mt-16 mb-48">
                  <Col
                    span={24}
                    md={8}
                    className="d-flex justify-content-center"
                  >
                    {isExpertComptable && shouldDisplayMigrationLink && (
                      <a className="text-tertiary cursor-pointer">
                        {t("offices.balanceAndStatistics.migration")}
                      </a>
                    )}
                  </Col>
                  <Col
                    span={24}
                    md={8}
                    className="d-flex justify-content-center"
                  >
                    {isExpertComptable && DISPLAY_PROMO_CODE_BUTTON && (
                      <BasicButton
                        text={t("offices.balanceAndStatistics.promoCode")}
                        onClick={() => setIsPromotionCodeModalVisible(true)}
                      />
                    )}
                  </Col>
                  <Col span={24} md={8} className="d-flex align-items-center">
                    <BasicButton
                      variant="tertiary"
                      disabled={!filledAddress || !fullActiveOffice.cgu}
                      text={t("offices.balanceAndStatistics.credit")}
                      data-test="button"
                      data-alt={t("offices.balanceAndStatistics.credit")}
                      onClick={() => setIsOrderModalVisible(true)}
                    />
                    {!cguAccepted && isExpertComptable && (
                      <InfoModal
                        value="insufficientCreditCgu"
                        warning
                        className="ml-8 mr-0"
                        info={false}
                      />
                    )}
                    {(!fullActiveOffice.billing?.address1 ||
                      !fullActiveOffice.billing.zipCode) &&
                      cguAccepted &&
                      isExpertComptable && (
                        <InfoModal
                          value="insufficientCreditECAddress"
                          linkText={t(
                            "modals.info.insufficientCreditECAddress.linkText",
                          )}
                          linkOnClick={() =>
                            navigate(
                              ROUTES.extranet.menu.offices.officeWithBillingAddress.generate(
                                office.id,
                              ),
                            )
                          }
                          warning
                          info={false}
                          className="ml-8 mr-0"
                        />
                      )}

                    {!cguAccepted && !isExpertComptable && (
                      <InfoModal
                        value="insufficientCreditCollaboratorCgu"
                        warning
                        info={false}
                        className="ml-8 mr-0"
                      />
                    )}
                    {(!fullActiveOffice.billing?.address1 ||
                      !fullActiveOffice.billing.zipCode) &&
                      cguAccepted &&
                      !isExpertComptable && (
                        <InfoModal
                          value="insufficientCreditCollaboratorAddress"
                          warning
                          info={false}
                          className="ml-8 mr-0"
                        />
                      )}
                  </Col>
                </Row>
                <SeeMoreTableWrapper
                  className="mt-16 mb-48"
                  id={"billTable_" + fullActiveOffice.id}
                  showWrapper={filteredBills.length > 3}
                >
                  <div className="mb-16">
                    <BasicTable
                      id={"billTable_" + fullActiveOffice.id}
                      pagination={false}
                      columns={billColumns()}
                      dataSource={filteredBills}
                    />
                  </div>
                </SeeMoreTableWrapper>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="pt-15 pb-30">
        <div className="p-sm-24">
          <div className="d-flex justify-content-end align-items-start flex-wrap gap-32">
            <div className="flex-grow-1">
              <h2>{t("offices.balanceAndStatistics.statistics.title")}</h2>
              <i>{t("offices.balanceAndStatistics.statistics.info")}</i>
              {FEATURE_HAS_BEEN_TESTED_AND_SHOULD_APPEAR && (
                <div className={"d-flex justify-content-start mt-2"}>
                  <BasicButton
                    className={"rounded balance-and-statistics-button"}
                    variant={statsType === "usage" ? "primary" : "secondary"}
                    text={t(
                      "offices.balanceAndStatistics.statistics.statsType.usage",
                    )}
                    onClick={() => setStatsType("usage")}
                  />
                  <BasicButton
                    className={"rounded balance-and-statistics-button"}
                    variant={statsType === "cost" ? "primary" : "secondary"}
                    text={t(
                      "offices.balanceAndStatistics.statistics.statsType.cost",
                    )}
                    onClick={() => setStatsType("cost")}
                  />
                </div>
              )}
            </div>
            <Dropdown
              overlayClassName="dropdown-filter-overlay overlay-statistics"
              menu={{
                items: YEARS.map((year, index) => {
                  return {
                    key: index,
                    onClick: () => setSelectedYear(year),
                    label: <div>{year}</div>,
                  };
                }),
              }}
              trigger={["click"]}
            >
              <BasicButton
                className="filters-dropdown-button w-auto"
                text={
                  <div className="d-flex align-items-center justify-content-center">
                    <span className="text-nowrap">
                      {t("offices.balanceAndStatistics.statistics.year") +
                        selectedYear}
                    </span>
                  </div>
                }
                icon={<ChevronDownIcon />}
              />
            </Dropdown>
          </div>
          <div className="mt-24 pt-24 pb-24">
            <BasicTable
              pagination={false}
              columns={statColumns(statsType === "cost")}
              dataSource={[
                {
                  key: "1",
                  title: t(
                    "offices.balanceAndStatistics.statistics.signatureStats",
                  ),
                  stats: filteredStats.map((stat): number =>
                    statsType === "usage"
                      ? stat.signatureCount
                      : stat.signaturesCost,
                  ),
                },
                {
                  key: "2",
                  title: t(
                    "offices.balanceAndStatistics.statistics.sealingStats",
                  ),
                  stats: filteredStats.map((stat): number =>
                    statsType === "usage"
                      ? stat.scellementCount
                      : stat.sealingsCost,
                  ),
                },
                {
                  key: "3",
                  title: t(
                    "offices.balanceAndStatistics.statistics.attachmentStats",
                  ),
                  stats: filteredStats.map((stat): number =>
                    statsType === "usage"
                      ? stat.attachmentCount
                      : stat.attachmentsCost,
                  ),
                },
              ]}
              className="table-primary"
            />
          </div>
        </div>
      </div>
      <PromotionCodeModal
        officeId={office.id}
        visible={isPromotionCodeModalVisible}
        onCancel={() => setIsPromotionCodeModalVisible(false)}
      />
    </>
  );
};

export default BalanceAndStatisticsTabContent;
